import {ChangeDetectorRef, Component, DoCheck, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {DateTime} from 'luxon';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {LastQuoteCacheService} from '../last-quote-cache.service';
import {MessageBusService} from '../message-bus.service';
import {makeOptionTicker, OptionType} from '../options-common/options.model';
import {QuoteDto} from '../shell-communication/dtos/quote-dto.class';
import {OptionExpirationDescriptor} from '../shell-communication/shell-dto-protocol';
import {DetectMethodChanges, isValidNumber, isVoid} from '../utils';
import {COPILOT_DISCLAIMER} from './copilot-disclaimer';
import {COPILOT_DISCLAIMER_ENABLED} from './copilot-disclaimer-visibility';
import {OpgTradeContent, OpgTradeSuggestion} from './opg-copilot-domain';
import {IOpgCopilotFacade} from './opg-copilot-facade';
import {computeOpgComboNet, OpgComboNet, opgLedgerPrice, OpgPricedLeg, opgOrderPrice} from './opg-copilot-pricing';

const MINUS = '−';   // proper minus sign (family typography, as throughout the copilot sources)

// THE ONE trade card of the OPG CoPilot (CR-20260710-001, phase 1) — serves every result
// member (all individually previewable card-by-card).
// Donor anatomy: the Hedge CoPilot solution card (collapsed face
// + click-to-expand + quote-live pricing) with the score in front (spec §6: "basically that
// with a score in front"; sketch frames 03/04).
//
// COURIER (§6/§10): the skill-authored content renders VERBATIM — the card never caps,
// filters, re-ranks, or reshapes it. Score is display-only (nothing structural, open #2).
// ONE action only: Preview <-> Un-Preview. NO Review button (§10 prohibition). The card
// never flips suggestion.state.previewed itself — it emits and the orchestrator owns the
// state + the load/border side-effects (§8 consistency rule).
//
// DATE-BOUND (amendment A5, hardened by CR-20260827-004): the trade NAMES its own expiration
// (content.expiration) — the card header shows that date AND every leg prices at it, always,
// on both hosts, previewed or not. Legs resolve through the host —
// root.readExpirationForTradeDate(content.expiration, leg.expirationOffset): 0/absent = the
// trade's own expiration; a non-zero offset = the calendarized business-day walk from it
// (the "+Nd" marker in the legs table). The panel's selected expiration plays NO role in
// pricing any more (it used to be the resolution anchor — which priced every HedgeMatrix
// Select card at the nearest date, the CR's reported bug). WI-6 preview navigation remains a
// LOADING concern only, and the loaded grid cell's price still wins once previewed (§9).
// Subscriptions follow ticker drift: ngDoCheck + onQuoteTick re-derive the leg tickers and
// diff-sync the ref-counted subscriptions when they change (chain reload, symbol switch).
//
// DEFAULT change detection (NOT OnPush) — the family's leaf-owns-CD rule for
// quote-ticking leaves: the parent mutates suggestion.state in place and quote ticks
// arrive outside any parent CD pass, so this leaf drives its OWN re-render — every
// 'QuoteDto' bus tick runs the empty @DetectMethodChanges handler below and the price
// getters re-pull fresh mids from the cache (mirrors HgCopilotSolutionCardComponent).
@Component({
    selector: 'ets-opg-trade-card',
    templateUrl: 'opg-trade-card.component.html',
    styleUrls: ['./opg-trade-card.component.scss']
})
export class OpgTradeCardComponent implements OnInit, DoCheck, OnDestroy {

    constructor(
        private readonly _changeDetector: ChangeDetectorRef,
        private readonly _lastQuoteCache: LastQuoteCacheService,
        private readonly _messageBus: MessageBusService
    ) {
    }

    // M8: the shared compliance disclaimer, rendered under the Preview action. A constant, never
    // reassigned — exposed as a field only because templates cannot import.
    readonly disclaimer = COPILOT_DISCLAIMER;

    // CR-20260723-003: global visibility switch for the disclaimer above (client-requested disable).
    // HIDDEN, not removed - flip COPILOT_DISCLAIMER_ENABLED to re-show the disclaimer at all five sites.
    readonly disclaimerEnabled = COPILOT_DISCLAIMER_ENABLED;

    private readonly _unsubscriber = new Subject<void>();

    private _suggestion: OpgTradeSuggestion;

    // Ref-counted tickers currently held with LastQuoteCacheService — wholesale-released on
    // destroy; everywhere else kept in sync with the CURRENT leg tickers by syncTickers()
    // (diff-based: trade switch, chain reload, symbol switch all flow through it).
    private _subscribedTickers: string[] = [];

    private _initialized = false;

    // The suggestion this card renders. Switching to a different trade (different id)
    // re-targets the quote subscriptions and collapses the card (cards arrive collapsed,
    // family rule). No output is emitted from this setter (echo-loop hazard rule);
    // previewToggled fires only from the user-driven button handler.
    @Input()
    set suggestion(value: OpgTradeSuggestion) {
        const previousId = this._suggestion ? this._suggestion.id : null;
        this._suggestion = value;
        const currentId = value ? value.id : null;
        if (this._initialized && currentId !== previousId) {
            this.expanded = false;
            this.syncTickers();
        }
    }

    get suggestion(): OpgTradeSuggestion {
        return this._suggestion;
    }

    // The host facade — resolves per-leg expiration descriptors at the trade's own date
    // (CR-20260827-004) and supplies the Default Qty the ledger price normalizes by.
    @Input() root: IOpgCopilotFacade;

    // The loaded grid cell's price for a previewed trade (sign/scale-final), or null while
    // unpriced (e.g. Live off — the trade "just sits there"). Wired by the orchestrator
    // from its loaded-trade registry. When it returns a number for a PREVIEWED card, it
    // WINS over the card's own quote feed — the grid is the tradeable reference (§9).
    @Input() loadedPriceOf: (suggestionId: string) => number | null;

    @Output() previewToggled = new EventEmitter<OpgTradeSuggestion>();

    // Cards arrive collapsed; clicking the header toggles the full detail.
    expanded = false;

    ngOnInit(): void {
        this._initialized = true;
        // Subscribe each leg's option ticker (ref-counted) so live quotes stream; re-render
        // on every quote tick by re-reading the cache from the template getters (the
        // platform pattern — HgCopilotSolutionCardComponent donor).
        this.syncTickers();
        this._messageBus.of<QuoteDto[]>('QuoteDto')
            .pipe(takeUntil(this._unsubscriber))
            .subscribe(() => this.onQuoteTick());
    }

    // TICKER DRIFT — SECONDARY check (ngDoCheck only runs when the parent view refreshes;
    // the PRIMARY check lives in onQuoteTick, whose bus subscription fires regardless of
    // the ancestors' CD topology). The list comparison keeps both no-ops until the resolved
    // leg tickers actually change (chain reload, symbol switch, late chain arrival).
    ngDoCheck(): void {
        this.syncTickers();
    }

    ngOnDestroy(): void {
        this.releaseTickers();
        this._unsubscriber.next();
        this._unsubscriber.complete();
    }

    // Every 'QuoteDto' bus tick forces a CD pass (decorator) and the price getters re-pull
    // fresh mids from the cache (leaf-owns-CD; donor recipe). PRIMARY ticker-drift check:
    // when the chain reloads or the symbol switches, the legs resolve to different tickers
    // and the held subscriptions are for the WRONG contracts; diff-sync. The bus tick is the
    // one heartbeat guaranteed to reach this leaf whatever the ancestors' CD state.
    @DetectMethodChanges()
    onQuoteTick(): void {
        this.syncTickers();
    }

    @DetectMethodChanges()
    toggleExpanded(): void {
        this.expanded = !this.expanded;
    }

    // User-driven only — the orchestrator flips suggestion.state.previewed after the
    // load/unload + border side-effects succeed (§8: card state, strategy, and border
    // move together; the card itself never flips the flag).
    onPreviewClick(): void {
        if (this._suggestion) {
            this.previewToggled.emit(this._suggestion);
        }
    }

    // ---- Collapsed face -----------------------------------------------------------------

    get previewed(): boolean {
        return !!this._suggestion && this._suggestion.state.previewed;
    }

    get previewLabel(): string {
        return this.previewed ? 'Un-Preview' : 'Preview';
    }

    // CR-20260924-002: why Preview is blocked right now (an ES card while the Hedge Matrix's ES
    // hedging is off), or null. Never blocks an Un-Preview; hosts without the member never block.
    get previewBlockedReason(): string | null {
        if (this.previewed || !this.root || !this.root.readPreviewBlockedReason) {
            return null;
        }
        const content = this.content();
        return content ? this.root.readPreviewBlockedReason(content.side, content) : null;
    }

    // Score is rendered ONLY when the skill provided one — display-only, nothing
    // structural depends on it (§6, open #2: the layout survives its removal).
    get hasScore(): boolean {
        const content = this.content();
        return !!content && isValidNumber(content.score);
    }

    get scoreText(): string {
        const content = this.content();
        return content ? '' + content.score : '';
    }

    get tradeName(): string {
        const content = this.content();
        return content ? content.name : '';
    }

    // Skill-authored risk value, VERBATIM (courier §6) — the risk CATEGORY the trade
    // belongs to (BF-4/A4: in a mixed-risk "All" run different trades carry different
    // categories, so each card says which one it is; the card adds the "Max Risk" label).
    // Both CR: '' on a follow-through in a mixed run (risk levels shape only the range
    // family) — the *ngIf drops the whole line, which IS the "untouched by risk" signal.
    get maxRiskText(): string {
        const content = this.content();
        return content ? content.maxRiskLabel : '';
    }

    // Both CR: the family tag of a MIXED (Both) run's candidate — the user is comparing
    // kinds, so each card names its own. Absent (empty) on a single-family run: those cards
    // render exactly as before. Verbatim courier — only the skill's own tag, prettified.
    get familyText(): string {
        const content = this.content();
        if (!content || content.tradeType == null) {
            return '';
        }
        return content.tradeType === 'range' ? 'Range' : 'Follow-Through';
    }

    get isRangeFamily(): boolean {
        const content = this.content();
        return !!content && content.tradeType === 'range';
    }

    // WI-8 (A5): the collapsed face shows the legs in ONE compact line, so the trader reads the
    // structure without expanding ("it's a click to get there — that's the problem"). Seated on
    // the card's BOTTOM line, level with the Max Risk row (CR-20260715-002; originally under the
    // name). Format = "(<signed qty>) <strike>" per leg, joined by " / " (owner, WI-8 smoke):
    // the qty SIGN carries the direction (+ long / - short — ASCII minus per owner, not the price
    // line's proper MINUS), so there are no "Buy"/"Sell" words — e.g. "(+10) 7470 / (-10) 7480".
    // SAME source as the expanded table — the moldRows walk — so the face line and the table can
    // never disagree on a leg's side/qty/strike; the magnitude is the engine's ABSOLUTE contract
    // count (contract v5, CR-20260827-002 — what executes; no Default Qty scaling anywhere on
    // the card any more). Inter-leg WIDTH rows are skipped here (owner: no widths
    // on the face; they still show in the expanded table), as are per-leg offset markers (the face
    // carries no new information beyond the table; the phase-1 mock never calendarizes). Empty
    // string when no legs resolve → the *ngIf drops the line (never a bare separator).
    //
    // WI-17/A9 — THIS LINE STAYS IN MOLD ORDER; the TABLE does not. Same rule ("calls read up,
    // puts read down"), two different axes: a horizontal line is read LEFT→RIGHT, so "up" means
    // ASCENDING strikes for calls (7550 → 7555) and "down" means DESCENDING for puts. Mold order
    // IS that order — the mold is anchor-first, and the anchor is the lowest-strike leg on calls
    // and the highest-strike leg on puts. So the face line was already correct on both sides
    // (owner, 2026-07-14) and must NOT follow the table's reversal.
    //
    // CR-20260715-003 — wrap safety: every space in the line is a NO-BREAK space (\u00A0)
    // EXCEPT the one after each "/". A leg unit "(±qty) strike" is atomic and holds on to the
    // separator that follows it, so the browser's own line-breaker can only break AFTER a
    // "/ " — a wrapped first line always ends with "/" and the next line always starts with a
    // whole unit (owner demo: "(+10) 7520 / (-20) 7503 /" ↵ "(+10) 7534"; a quantity must never
    // separate from its strike). Title/tooltip consumers render NBSP as a plain space, so this
    // one string serves the face AND the hover text.
    get legsText(): string {
        const parts: string[] = [];
        const rows = this.moldRows();
        for (let i = 0; i < rows.length; i++) {
            const row = rows[i];
            if (row.kind === 'leg') {
                const sign = row.side === 'Sell' ? '-' : '+';   // short = - / long = + (ASCII per owner)
                parts.push(`(${sign}${row.qty})\u00A0${row.strike}`);
            }
        }
        return parts.join('\u00A0/ ');
    }

    // ---- Live price (§6: prices tick live BEFORE any preview) ----------------------------
    //
    // THE HEADLINE IS THE LEDGER PRICE (owner, 2026-07-14 / WI-18): net / Default Qty — "the price
    // normalized to the default quantity". It carries no label because it needs none: it is simply
    // THE price of the trade, and it is the ONE number that must agree with the OPG grid's
    // strategy-column price (the grid computes the same quantity — see opg-copilot-pricing.ts).
    // The expanded table's footer carries the OTHER price, the order price. Do not conflate them.
    //
    // Precedence:
    //   1. previewed AND the loaded grid cell is priced -> the grid price WINS (§9: the grid is the
    //      tradeable reference). No conflict with the rule above — the grid cell IS the ledger price;
    //   2. else the all-or-nothing ledger price off the per-leg quote mids (blank while ANY leg's
    //      quote is still missing — never a partial price);
    //   3. else the skill's priceAtSelection — the pre-first-tick fallback ONLY. Since contract
    //      v5 (CR-20260827-002) it is the net of the ABSOLUTE combo (the engine's quantities are
    //      what executes), so it must be divided by the Default Qty to land on the ledger scale;
    //      with no usable Default Qty there is no ledger scale to land on and the fallback
    //      blanks rather than print a wrong-scale number.
    get price(): number | null {
        if (this.previewed && this.loadedPriceOf && this._suggestion) {
            const loaded = this.loadedPriceOf(this._suggestion.id);
            if (isValidNumber(loaded)) {
                return loaded;
            }
        }
        const ledger = opgLedgerPrice(this.comboNet(), this.defaultQty());
        if (ledger !== null) {
            return ledger;
        }
        const content = this.content();
        const dq = this.defaultQty();
        if (content && isValidNumber(content.priceAtSelection) && isValidNumber(dq) && dq > 0) {
            return content.priceAtSelection / dq;
        }
        return null;
    }

    // ORDER price — net / HCF of the trade's legs, with NO Default-Qty normalization (owner: "order
    // price without normalizing to the default quantity"). Rendered in the expanded table's
    // "Total (Order Price)" footer, the Hedge solution card's own footer.
    //
    // DELIBERATELY has no priceAtSelection fallback (unlike the headline): the skill quotes a
    // LEDGER-scale price, so falling back to it here would print a ledger number under an "Order
    // Price" label — a wrong number wearing the right label, which is worse than a blank. Until every
    // leg has a quote this stays an em dash, exactly as the Hedge donor's total does.
    get orderPrice(): number | null {
        return opgOrderPrice(this.comboNet());
    }

    get isCredit(): boolean {
        return this.isCreditPrice(this.price);
    }

    get isDebit(): boolean {
        return this.isDebitPrice(this.price);
    }

    // Sign -> credit/debit colouring for ANY price cell — the headline, each leg's Price cell, and the
    // total (owner: "colour the price column just as the hedging grid: negative red, positive green").
    isCreditPrice(value: number | null | undefined): boolean {
        return isValidNumber(value) && value > 0;
    }

    isDebitPrice(value: number | null | undefined): boolean {
        return isValidNumber(value) && value < 0;
    }

    // Signed family format "+$16.90" / "−$5.30" (the APG card's costText idiom — BF-4: the sign IS the
    // credit/debit indicator, no 'cr'/'db' suffix); em dash while nothing is priceable yet. Shared by
    // the headline, the per-leg Price cells and the total, so the three can never format differently.
    get priceText(): string {
        return this.formatMoney(this.price);
    }

    get orderPriceText(): string {
        return this.formatMoney(this.orderPrice);
    }

    // The Price cell of one leg row (WI-18). Width rows carry no price and render blank.
    legPriceText(row: LegTableRow): string {
        return this.formatMoney(row && isValidNumber(row.price) ? row.price : null);
    }

    private formatMoney(value: number | null): string {
        if (value === null || !isValidNumber(value)) {
            return '—';
        }
        return (value >= 0 ? '+$' : MINUS + '$') + Math.abs(value).toFixed(2);
    }

    // "Fri 10-Jul-26 (0d)" — the expiration the trade is BOUND TO (A5): its OWN `expiration`
    // field (ranked trades in a range may differ), in the APG card's date format incl. the
    // days-to-expiration badge. The (Nd) badge comes from the matching visible descriptor;
    // if the trade's date is not in the current chain (e.g. mid symbol-switch) the badge is
    // dropped and the raw date still shows.
    get expirationText(): string {
        const content = this.content();
        if (!content || !content.expiration) {
            return '';
        }
        const dt = DateTime.fromFormat(content.expiration, 'yyyy-MM-dd');
        const pretty = dt.isValid ? dt.toFormat('EEE dd-MMM-yy') : content.expiration;
        const descriptor = this.findExpirationDescriptor(content.expiration);
        const days = descriptor && isValidNumber(descriptor.daysToExpiration)
            ? ' (' + descriptor.daysToExpiration + 'd)'
            : '';
        return pretty + days;
    }

    // The visible expiration descriptor whose key matches the trade's bound date, or null
    // (date not in the current chain). Used only for the days-to-expiration badge.
    private findExpirationDescriptor(key: string): OptionExpirationDescriptor | null {
        if (!this.root) {
            return null;
        }
        const list = this.root.readAvailableExpirations() || [];
        for (let i = 0; i < list.length; i++) {
            if (list[i].optionExpirationDate === key) {
                return list[i];
            }
        }
        return null;
    }

    // ---- Expanded detail ------------------------------------------------------------------

    // The legs table, mimicking the OPG strategy-builder/template read-only (BF-4/A4:
    // "mimic this table here on the left, but without the inputs"): leg rows (Side / Qty /
    // Strike / Exp. Offset when present) interleaved with the inter-leg DISTANCE rows
    // (owner A4: do NOT fold distances into a per-leg column — still holds). Meeting
    // 2026-07-15 [01:07-01:08] supersedes A4's column placement: the distance renders in the
    // STRIKE column, between the two strikes it separates, per the Options Pricing Pad
    // leg-distance convention — in the Qty column it read as a quantity ("10 / 15 / 10 —
    // what is that 15?"). Displayed qty = mold leg.qty * the grid's Default Qty
    // (§14 qty convention; matches PricingGridStrategyColumn leg qty scaling). Calendarized
    // legs (non-zero expirationOffset) show the grid's "+Nd" business-day marker (A2);
    // 0/absent shows NOTHING (never a "0d").
    //
    // WI-17/A9 — ROW ORDER IS STRIKE-DESCENDING, NOT MOLD ORDER (supersedes A4's "mold order"
    // for this table). The hard rule: a leg listing must read in the SAME strike direction as
    // the surface it is read against, and this table's axis is VERTICAL — the OPG grid's own
    // axis, which sorts strikes descending. So the highest strike is the top row on BOTH sides.
    //
    // In practice this reverses CALLS only: the mold is anchor-first, and the anchor is the
    // LOWEST-strike leg on calls (they "read up") but the HIGHEST-strike leg on puts (they
    // "read down") — so the put walk already emits descending and is left exactly as it was.
    // Reversing the WHOLE row array (widths included) is what keeps the layout honest: a width
    // row separates the same two legs after the reversal as before it (… A, w1, B, w2, C …
    // becomes … C, w2, B, w1, A …), so no distance is re-attributed to the wrong pair.
    //
    // Display only. The mold descriptor handed to addStrategy, content.startingStrike, the
    // anchor leg and the X border are all untouched — a call vertical is still long the lower
    // strike and short the higher one; it is only READ from the top down now.
    get legRows(): LegTableRow[] {
        const rows = this.moldRows();
        const content = this.content();
        const readsUp = !!content && content.side === 'calls';
        const ordered = readsUp ? rows.reverse() : rows;   // moldRows() returns a fresh array — safe to reverse in place

        // WI-18: the Price column is resolved HERE and not inside moldRows(), because moldRows() is
        // ALSO the collapsed face's legsText walk — and the face line has no prices. Resolving them in
        // the shared walk would make every collapsed card hit the quote cache for every leg on every
        // quote tick, to compute a number it then throws away. This getter only runs while a card is
        // expanded. Reversal is safe: a price rides on its own leg row, so it cannot be re-attributed.
        for (let i = 0; i < ordered.length; i++) {
            const row = ordered[i];
            if (row.kind === 'leg') {
                row.price = this.legSignedMid(row.side, row.strike, row.expirationOffset);
            }
        }
        return ordered;
    }

    // The mold walk itself, in MOLD ORDER (anchor-first): the single source both the table and
    // the card-face legs line resolve their side/qty/strike from. The two differ only in the
    // ORDER they present it in (WI-17/A9): the horizontal face line keeps this order, the
    // vertical table reverses it for calls. Always returns a fresh array.
    private moldRows(): LegTableRow[] {
        const content = this.content();
        if (!content || !content.legs || !isValidNumber(content.startingStrike)) {
            return [];
        }
        const stepSign = content.side === 'calls' ? 1 : -1;
        let cursor = content.startingStrike;
        const rows: LegTableRow[] = [];
        for (let i = 0; i < content.legs.length; i++) {
            const entry = content.legs[i];
            if (entry.type === 'width') {
                const width = isValidNumber(entry.width) ? entry.width : 0;
                rows.push({kind: 'width', width});
                cursor = cursor + stepSign * width;
            } else if (entry.type === 'leg') {
                // CR-20260827-002: engine quantities are ABSOLUTE — the card shows what executes.
                const qty = isValidNumber(entry.qty) ? entry.qty : 1;
                rows.push({
                    kind: 'leg',
                    side: entry.side,
                    qty: Math.abs(qty),
                    strike: cursor,
                    expirationOffset: isValidNumber(entry.expirationOffset) ? entry.expirationOffset : 0,
                    offsetText: this.offsetBadge(entry.expirationOffset)
                });
            }
        }
        return rows;
    }

    // One leg's signed cash-flow price at its resolved strike + expiration (WI-18): a SELL receives
    // premium (credit, +), a BUY pays it (debit, -). Same sign rule as the combo net, so a leg's own
    // Price cell can never contradict the total it rolls up into. null while the quote is still
    // streaming (or the leg cannot resolve) -> the cell shows an em dash.
    private legSignedMid(side: 'Buy' | 'Sell', strike: number, expirationOffset: number): number | null {
        const content = this.content();
        if (!content || !isValidNumber(strike)) {
            return null;
        }
        const descriptor = this.legExpiration(isValidNumber(expirationOffset) ? expirationOffset : 0);
        if (!descriptor) {
            return null;
        }
        const optionType = content.side === 'calls' ? OptionType.Call : OptionType.Put;
        const ticker = makeOptionTicker(descriptor, optionType, strike, 'American');
        if (!ticker) {
            return null;
        }
        const quote = this._lastQuoteCache.getLastQuote(ticker);
        if (isVoid(quote) || !isValidNumber(quote.mid)) {
            return null;
        }
        return side === 'Sell' ? quote.mid : -quote.mid;
    }

    // Gates the Exp. Offset column — shown only when a leg actually carries an offset
    // (BF-4: "the expiration offset, if it's present"; the de-calendarized mock never does).
    //
    // Answers straight off the MOLD, deliberately not off legRows(): the template evaluates this once
    // per column header AND once per row, and since WI-18 a legRows() walk also hits the quote cache
    // for every leg — routing this through it would multiply those lookups by the row count on every
    // quote tick, for a question the strike walk and the prices have no bearing on.
    get hasLegOffsets(): boolean {
        const content = this.content();
        if (!content || !content.legs) {
            return false;
        }
        for (let i = 0; i < content.legs.length; i++) {
            const entry = content.legs[i];
            if (entry.type === 'leg' && !!this.offsetBadge(entry.expirationOffset)) {
                return true;
            }
        }
        return false;
    }

    // ---- Internals --------------------------------------------------------------------------

    private content(): OpgTradeContent {
        return this._suggestion ? this._suggestion.content : null;
    }

    // The RAW Default Qty (null when the panel has none) — deliberately NOT displayQtyFactor(), whose
    // 1-fallback would look like a real divisor. opgLedgerPrice has to be able to SEE that there is
    // nothing to normalize to, so it can degrade to the order price instead of silently dividing by 1.
    private defaultQty(): number | null {
        const dq = this.root ? this.root.readDefaultQty() : null;
        return isValidNumber(dq) ? dq : null;
    }

    // The grid's calendarized-leg badge: "+2d" / "-1d"; '' for 0/absent (= the trade's own date).
    private offsetBadge(offset: number): string {
        if (!isValidNumber(offset, true)) {
            return '';
        }
        return offset < 0 ? offset + 'd' : '+' + offset + 'd';
    }

    // Resolve the strike-less mold to concrete per-leg strikes by walking content.legs
    // from content.startingStrike: each 'leg' entry takes the current cursor strike; each
    // 'width' entry advances the cursor — UP for calls, DOWN for puts (§7 reading rule;
    // mirrors PricingGridStrategyColumn.setupColumnForOpgMode anchoring).
    private walkLegs(): ResolvedLeg[] {
        const content = this.content();
        if (!content || !content.legs || !isValidNumber(content.startingStrike)) {
            return [];
        }
        const stepSign = content.side === 'calls' ? 1 : -1;
        let cursor = content.startingStrike;
        const out: ResolvedLeg[] = [];
        for (let i = 0; i < content.legs.length; i++) {
            const entry = content.legs[i];
            if (entry.type === 'width') {
                cursor = cursor + stepSign * (isValidNumber(entry.width) ? entry.width : 0);
            } else if (entry.type === 'leg') {
                out.push({
                    strike: cursor,
                    side: entry.side,
                    qty: entry.qty,
                    expirationOffset: isValidNumber(entry.expirationOffset) ? entry.expirationOffset : 0
                });
            }
        }
        return out;
    }

    // A leg's expiration descriptor at the TRADE'S OWN bound date (CR-20260827-004):
    // content.expiration resolved against the host's visible chain, 0/absent offset = that
    // expiration itself, non-zero = the calendarized business-day walk from it. null when
    // unresolvable (date not in the current chain / no root yet) — the price then
    // blanks/falls back (all-or-nothing).
    private legExpiration(offset: number): OptionExpirationDescriptor {
        const content = this.content();
        if (!this.root || !content || !content.expiration) {
            return null;
        }
        // CR-20260924-002: an ES trade's legs live on ITS contract's chain, whatever the host's
        // toggle says now (a card must never silently re-price on the SPX chain after a flip).
        return this.root.readExpirationForTradeDate(content.expiration, offset, content.esContract || null);
    }

    // CR-20260924-002: the totals normalise an ES leg toward the host's base (× 0.5 on an SPX
    // book) — the convention every Hedge Matrix card follows; the per-leg Price cells stay raw.
    // 1 on a host without the member, and on every SPX leg.
    private legNormFactor(descriptor: OptionExpirationDescriptor): number {
        if (!this.root || !this.root.readLegNormFactor || !descriptor) {
            return 1;
        }
        const factor = this.root.readLegNormFactor(descriptor.underlyingTicker || null);
        return isValidNumber(factor, true) ? factor : 1;
    }

    // Per-leg option tickers at each leg's RESOLVED expiration, deduped. 'American'
    // explicitly — the exact cache keys the OPG's own rows use (pricing-grid.row.ts
    // makeRowTickers), so the card and the grid share subscriptions.
    private legTickers(): string[] {
        const content = this.content();
        if (!content) {
            return [];
        }
        const optionType = content.side === 'calls' ? OptionType.Call : OptionType.Put;
        const legs = this.walkLegs();
        const out: string[] = [];
        for (let i = 0; i < legs.length; i++) {
            const descriptor = this.legExpiration(legs[i].expirationOffset);
            if (!descriptor) {
                continue;
            }
            const ticker = makeOptionTicker(descriptor, optionType, legs[i].strike, 'American');
            if (ticker && out.indexOf(ticker) === -1) {
                out.push(ticker);
            }
        }
        return out;
    }

    // The trade's legs, resolved AND priced, in the quantities it is ACTUALLY placed in — since
    // contract v5 (CR-20260827-002) the engine's leg quantities ARE the executing quantities
    // (absolute contract counts; the grid's strategy column loads them unscaled behind the
    // descriptor's absoluteQty marker), so no × Default Qty here any more.
    //
    // THE SCALE IS LOAD-BEARING: opgLedgerPrice divides by Default Qty, and the grid column's
    // hcfMultiplier algebra cancels the HCF, so both still land on net / Default Qty — the
    // headline's match with the grid cell survives (see opg-copilot-pricing.ts).
    //
    // ALL-OR-NOTHING: every leg must resolve its expiration, its ticker AND have a cached mid, else
    // null — a partially-priced combo is a blank, never a partial price (the platform getGroupTotal
    // rule, and the Hedge donor's own contract).
    private pricedLegs(): OpgPricedLeg[] | null {
        const content = this.content();
        const legs = this.walkLegs();
        if (!content || legs.length === 0) {
            return null;
        }
        const optionType = content.side === 'calls' ? OptionType.Call : OptionType.Put;
        const out: OpgPricedLeg[] = [];
        for (let i = 0; i < legs.length; i++) {
            const leg = legs[i];
            if (!isValidNumber(leg.qty, true)) {
                return null;   // falseOnZero: a 0-qty leg is degenerate — never invent a quantity
            }
            const descriptor = this.legExpiration(leg.expirationOffset);
            if (!descriptor) {
                return null;
            }
            const ticker = makeOptionTicker(descriptor, optionType, leg.strike, 'American');
            if (!ticker) {
                return null;
            }
            const quote = this._lastQuoteCache.getLastQuote(ticker);
            if (isVoid(quote) || !isValidNumber(quote.mid)) {
                return null;   // all-or-nothing, like the platform getGroupTotal
            }
            out.push({side: leg.side, qty: Math.abs(leg.qty), mid: quote.mid * this.legNormFactor(descriptor)});
        }
        return out;
    }

    // The signed net + the HCF of the trade's ACTUAL leg quantities — the ONE source both prices come
    // from (headline = net / Default Qty; total = net / HCF), so the two can never be computed off
    // different legs or a different scale.
    private comboNet(): OpgComboNet | null {
        return computeOpgComboNet(this.pricedLegs());
    }

    // Sync the held subscriptions to the CURRENT leg tickers via the cache's diff primitive:
    // every unsubscribe is paired with a prior subscribe (ref-count safe — an unpaired
    // release on a shared slot freezes sibling surfaces), and unchanged tickers are not
    // touched, so a resync never blinks a price that was already streaming. The ONE
    // subscription mechanism for init, trade switch, and both drift heartbeats.
    private syncTickers(): void {
        if (!this._initialized) {
            return;
        }
        const tickers = this.legTickers();
        if (this.sameTickers(tickers, this._subscribedTickers)) {
            return;
        }
        this._lastQuoteCache.subscribeTickersDiff(this._subscribedTickers, tickers);
        this._subscribedTickers = tickers;
    }

    // Positional compare is sufficient: legTickers() is deterministic-order and deduped.
    private sameTickers(a: string[], b: string[]): boolean {
        if (a.length !== b.length) {
            return false;
        }
        for (let i = 0; i < a.length; i++) {
            if (a[i] !== b[i]) {
                return false;
            }
        }
        return true;
    }

    // Destroy-only wholesale release (everything held goes at once; no successor list to diff).
    private releaseTickers(): void {
        if (this._subscribedTickers.length > 0) {
            this._lastQuoteCache.unsubscribeTickers(this._subscribedTickers);
        }
        this._subscribedTickers = [];
    }
}

// A mold 'leg' entry resolved to its concrete strike by the startingStrike walk.
interface ResolvedLeg {
    strike: number;
    side: 'Buy' | 'Sell';
    qty: number;
    expirationOffset: number;
}

// One row of the expanded legs table (BF-4): a 'leg' row (Side/Qty/Strike/Price/offset marker,
// '' = none) or a 'width' row carrying the inter-leg distance — the builder's own
// interleaved leg/width structure, rendered read-only (the distance shows in the Strike
// column, Options-Pricing-Pad style — meeting 2026-07-15).
export interface LegTableRow {
    kind: 'leg' | 'width';
    side?: 'Buy' | 'Sell';
    qty?: number;
    strike?: number;
    offsetText?: string;
    width?: number;

    // The leg's RAW expiration offset (0 = the trade's own expiration), kept alongside the rendered
    // offsetText badge so the Price column can re-resolve the leg's expiration without re-parsing it.
    expirationOffset?: number;

    // WI-18: the leg's signed live price (Sell = credit +, Buy = debit −); null while its quote is
    // still streaming. Width rows carry none — they are a distance, not a tradeable leg. Filled by
    // legRows() (the expanded table), NOT by moldRows() — see the note there.
    price?: number | null;
}
